import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminNotificationDispatchDetail } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const readOnlyGridStyle = {
  display: 'grid',
  gap: '0.85rem',
};

const ReadOnlyField = ({ label, value, multiline = false, rows = 4 }) => {
  const fieldValue = value || '-';

  return (
    <label className="c-form__field">
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea
          className="c-form__control c-form__control--textarea"
          readOnly
          rows={rows}
          value={fieldValue}
        />
      ) : (
        <input className="c-form__control" readOnly type="text" value={fieldValue} />
      )}
    </label>
  );
};

const AdminNotificationDetailPage = () => {
  const content = yowanPageContent['/admin/notifications/:dispatchId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { dispatchId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminNotificationDispatchDetail(dispatchId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('通知履歴を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dispatchId]);

  if (!content) {
    return null;
  }

  if (!record) {
    return (
      <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
        <AdminPageHeader
          title={content.header.title}
          paragraphs={isLoading ? [] : [loadError || '指定された通知履歴は見つかりませんでした。']}
          actions={[
            <Link key="back-to-notifications" to={appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch)}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? <LoadingSpinner /> : null}
      </AdminBlogShell>
    );
  }

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={`${record.categoryLabel} / ${record.eventLabel}`}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-notifications" to={appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
          record.policyHref ? (
            <Link key="notification-policy" to={appendYowanPreviewQueryIfNeeded(record.policyHref, preservedDomainSearch)}>
              関連する通知ルール
            </Link>
          ) : null,
        ]}
      />

      <section className="yowan-admin-form c-surface-content" style={readOnlyGridStyle}>
        <ReadOnlyField label="履歴ID" value={record.id} />
        <ReadOnlyField label="種別" value={record.categoryLabel} />
        <ReadOnlyField label="内容" value={record.eventLabel} />
        <ReadOnlyField label="通知種別キー" value={record.eventKey} />
        <ReadOnlyField label="関連ルール" value={record.policyEventKey} />
        <ReadOnlyField label="状態" value={record.statusLabel} />
        <ReadOnlyField label="送信処理" value={record.sendStageLabel} />
        <ReadOnlyField label="送信結果" value={record.sendResultLabel} />
        <ReadOnlyField label="送信方法" value={record.channelLabel} />
        <ReadOnlyField label="送信区分" value={record.deliveryModeLabel} />
        <ReadOnlyField label="配送追跡" value={record.providerDeliveryTrackedLabel} />
        <ReadOnlyField label="配送状態" value={record.providerDeliveryStatusLabel} />
        <ReadOnlyField label="開封/クリック追跡" value={record.providerEngagementTrackedLabel} />
        <ReadOnlyField label="開封/クリック状態" value={record.providerEngagementStatusLabel} />
        <ReadOnlyField label="最終反応" value={record.providerEngagementLastLabel} />
        <ReadOnlyField
          label="反応サマリ"
          value={record.providerEngagementSummary || 'なし'}
          multiline
          rows={3}
        />
        <ReadOnlyField label="テンプレート" value={record.templateKey} />
        <ReadOnlyField label="送信元" value={record.sourceLabel} />
        <ReadOnlyField label="発生元" value={record.triggerPoint} />
        <ReadOnlyField label="依頼種別" value={record.onboardingRequestKindLabel} />
        <ReadOnlyField label="対象種別" value={record.onboardingTargetKindLabel} />
        <ReadOnlyField label="本人入力依頼ID" value={record.onboardingRequestId} />
        <ReadOnlyField label="処理日時" value={record.processedAt} />
        <ReadOnlyField label="宛先" value={record.recipientSummary} />
        <ReadOnlyField
          label="宛先内訳"
          value={record.hasRecipientBreakdown ? record.recipientBreakdownSummary : 'なし'}
        />
        <ReadOnlyField label="件名" value={record.subject} />
        <ReadOnlyField label="本文" value={record.renderedBody} multiline rows={8} />
        <ReadOnlyField
          label="宛先詳細"
          value={record.resolvedRecipientsJson}
          multiline
          rows={8}
        />
        <ReadOnlyField
          label="宛先内訳詳細"
          value={record.hasRecipientBreakdown ? record.recipientBreakdownJson : 'なし'}
          multiline
          rows={8}
        />
        <ReadOnlyField
          label="送信元詳細"
          value={record.triggerContextJson}
          multiline
          rows={8}
        />
        <ReadOnlyField
          label="アプリ送信処理エラー"
          value={record.lastErrorMessage || 'なし'}
          multiline
          rows={3}
        />
        <ReadOnlyField
          label="プロバイダー配送エラー"
          value={record.providerDeliveryErrorMessage || 'なし'}
          multiline
          rows={3}
        />
        <ReadOnlyField
          label="エラー内容"
          value={record.errorSummary}
          multiline
          rows={5}
        />
      </section>
    </AdminBlogShell>
  );
};

export default AdminNotificationDetailPage;
